import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import GuideLead from '../components/GuideLead';

const GuideCVTech = () => {
    return (
        <div className="min-h-screen bg-slate-200">
            <Helmet>
                <title>CV Tech/IT : Guide Spécifique pour Développeurs et Tech 2025</title>
                <meta name="description" content="Guide complet pour créer un CV tech/IT optimisé : sections spécifiques (GitHub, stack technique), compétences techniques, portfolio, certifications, exemples par spécialité." />
                <meta name="keywords" content="CV développeur, CV tech, CV informatique, CV IT, CV programmeur, CV data scientist, CV ingénieur logiciel" />
                <link rel="canonical" href="https://www.cv-analyse.fr/guide/cv-tech" />
                <script type="application/ld+json">{`
                {
                  "@context": "https://schema.org",
                  "@type": "HowTo",
                  "name": "Comment créer un CV tech et développeur efficace",
                  "description": "Construisez un CV technique optimisé pour les recruteurs IT et les ATS : stack, GitHub, certifications, mots-clés tech.",
                  "step": [
                    {"@type": "HowToStep", "name": "Lister son stack technique", "text": "Organisez vos langages, frameworks et outils par niveau de maîtrise."},
                    {"@type": "HowToStep", "name": "Mettre en avant ses projets GitHub", "text": "Ajoutez un lien vers vos projets open source ou personnels les plus significatifs."},
                    {"@type": "HowToStep", "name": "Ajouter ses certifications techniques", "text": "AWS, Google Cloud, Azure, certifications Agile ou sécurité selon votre spécialité."},
                    {"@type": "HowToStep", "name": "Utiliser les bons mots-clés tech", "text": "Adaptez les termes selon la spécialité : DevOps, Data, Front-end, Back-end, IA."},
                    {"@type": "HowToStep", "name": "Valider le score ATS de son CV", "text": "Testez votre CV tech avec CV Analyse pour vous assurer qu'il passe les filtres ATS."}
                  ]
                }
                `}</script>
            </Helmet>

            <nav aria-label="Fil d'Ariane" className="pt-16 pb-4">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <ol className="flex items-center space-x-2 text-sm text-slate-600">
                        <li><Link to="/" className="hover:text-white transition-colors">Accueil</Link></li>
                        <li className="text-slate-400">/</li>
                        <li><Link to="/guides" className="hover:text-white transition-colors">Guides</Link></li>
                        <li className="text-slate-400">/</li>
                        <li className="text-white">CV Tech</li>
                    </ol>
                </div>
            </nav>

            <div className="bg-slate-900 pt-4 pb-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-3xl mb-6">
                            <span className="text-4xl">💻</span>
                        </div>
                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                            CV Tech/IT : Guide Spécifique pour Développeurs
                        </h1>
                        <p className="text-xl md:text-2xl text-white max-w-3xl mx-auto leading-relaxed font-medium">
                            Optimisez votre CV tech avec les sections spécifiques, compétences techniques et bonnes pratiques du secteur
                        </p>
                    </div>
                </div>
            </div>

            <GuideLead slug="cv-tech" />

            <article className="px-6 py-16 mx-auto max-w-5xl">
                <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100 mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">🛠️ Sections spécifiques pour CV Tech</h2>
                    <div className="space-y-6">
                        <div className="border-l-4 border-slate-500 pl-6">
                            <h3 className="text-xl font-bold text-gray-900 mb-3">1. Stack technique</h3>
                            <p className="text-gray-700 mb-3">Listez vos technologies maîtrisées par catégorie :</p>
                            <ul className="space-y-2 text-gray-700">
                                <li>• Langages de programmation (JavaScript, Python, Java, etc.)</li>
                                <li>• Frameworks et bibliothèques (React, Vue, Angular, Django, etc.)</li>
                                <li>• Bases de données (PostgreSQL, MongoDB, Redis, etc.)</li>
                                <li>• Outils DevOps (Docker, Kubernetes, CI/CD, etc.)</li>
                                <li>• Cloud (AWS, Azure, GCP)</li>
                            </ul>
                        </div>
                        <div className="border-l-4 border-slate-500 pl-6">
                            <h3 className="text-xl font-bold text-gray-900 mb-3">2. Projets GitHub</h3>
                            <p className="text-gray-700">Mentionnez votre profil GitHub et les projets open-source significatifs. Les recruteurs tech consultent souvent GitHub pour évaluer la qualité du code.</p>
                        </div>
                        <div className="border-l-4 border-slate-500 pl-6">
                            <h3 className="text-xl font-bold text-gray-900 mb-3">3. Certifications techniques</h3>
                            <p className="text-gray-700">Mettez en avant les certifications pertinentes : AWS Certified, Google Cloud, Microsoft Azure, Kubernetes, etc.</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100 mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">📊 Mots-clés par spécialité</h2>
                    <div className="space-y-6">
                        <div>
                            <h3 className="text-xl font-bold text-gray-900 mb-3">Frontend Developer</h3>
                            <div className="flex flex-wrap gap-2">
                                {['React', 'Vue.js', 'Angular', 'TypeScript', 'HTML5', 'CSS3', 'SASS', 'Webpack', 'Vite'].map(tech => (
                                    <span key={tech} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">{tech}</span>
                                ))}
                            </div>
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-gray-900 mb-3">Backend Developer</h3>
                            <div className="flex flex-wrap gap-2">
                                {['Node.js', 'Python', 'Java', 'Spring', 'Django', 'REST API', 'GraphQL', 'Microservices'].map(tech => (
                                    <span key={tech} className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">{tech}</span>
                                ))}
                            </div>
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-gray-900 mb-3">DevOps Engineer</h3>
                            <div className="flex flex-wrap gap-2">
                                {['Docker', 'Kubernetes', 'Terraform', 'Ansible', 'Jenkins', 'GitLab CI', 'AWS', 'Azure'].map(tech => (
                                    <span key={tech} className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">{tech}</span>
                                ))}
                            </div>
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-gray-900 mb-3">Data Scientist</h3>
                            <div className="flex flex-wrap gap-2">
                                {['Python', 'R', 'SQL', 'Machine Learning', 'TensorFlow', 'PyTorch', 'Pandas', 'NumPy'].map(tech => (
                                    <span key={tech} className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm">{tech}</span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100 mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">💡 Conseils spécifiques au secteur Tech</h2>
                    <ul className="space-y-4 text-gray-700">
                        <li className="flex items-start"><span className="text-slate-600 mr-3">•</span><strong>Portfolio vs CV :</strong> Le portfolio (GitHub, site perso) est souvent plus important que le CV dans le tech</li>
                        <li className="flex items-start"><span className="text-slate-600 mr-3">•</span><strong>Projets personnels :</strong> Mettez en avant vos projets open-source ou personnels significatifs</li>
                        <li className="flex items-start"><span className="text-slate-600 mr-3">•</span><strong>Niveau de maîtrise :</strong> Indiquez votre niveau (Junior, Confirmé, Senior) pour chaque technologie</li>
                        <li className="flex items-start"><span className="text-slate-600 mr-3">•</span><strong>Méthodologies :</strong> Mentionnez Agile, Scrum, TDD, Clean Code si pertinent</li>
                        <li className="flex items-start"><span className="text-slate-600 mr-3">•</span><strong>Contributions :</strong> Si vous contribuez à des projets open-source, mentionnez-le</li>
                    </ul>
                </div>

                <div className="bg-gradient-to-r from-slate-700 to-gray-700 rounded-3xl p-8 text-center text-white mb-16">
                    <h2 className="text-3xl font-bold mb-4">Votre stack technique est-il mis en valeur comme il le mérite ?</h2>
                    <p className="text-xl mb-6">Notre IA analyse votre CV tech et identifie les compétences sous-valorisées qui font la différence — score et 3 conseils offerts</p>
                    <Link to="/" className="inline-block bg-white text-slate-700 font-bold px-8 py-4 rounded-2xl hover:bg-gray-100 transition-colors">
                        Analyser mon CV tech gratuitement
                    </Link>
                </div>
            </article>
        </div>
    );
};

export default GuideCVTech;








